import React from 'react';
import styled from 'styled-components';
import { ApolloClient, useApolloClient } from '@apollo/react-hooks';

import { formatCurrencyAmount } from '../../../Locales';
import { AnimatedGradientBackground } from '../../../components/common/GradientBackground';
import { MerchantProductOffer } from '../../../lib/productDiscovery/conversationMessages';
import {
    CLICKABLE_ELEMENT_CSS_MIXIN,
    NON_HIGHLIGHTABLE_NOR_DRAGGABLE_ELEMENT_CSS_MIXIN,
} from '../../../style/styleConstants';
import { colors } from '../../../tokens/theme';
import { font } from '../../../style/text';
import { deviceWidthMeasurements, getPixelNumberFromDeviceWidthMeasurement } from '../../../style/size';
import { useProductImage } from '../../../lib/productDiscovery/productImages';
import { isMobileDevice } from '../../../style/utils';
import { logUserEventUtil } from '../../../lib/events/userEvents';
import { MerchantIdToMerchantNameMap } from '../../../lib/productDiscovery/displayLogic';
import { Feature, useHasFeature } from '../../../lib/features/buildFeatureMap';
import {
    handleSaveMerchantProductOffer,
    handleUnsaveMerchantProductOffer,
    useSaveMerchantProductOffer,
    useUnsaveMerchantProductOffer,
} from '../../../lib/productDiscovery/savedMerchantProductOffers';

const productLinkArrow = '/assets/images/icons/product-link-arrow.svg';
const emptyFavoriteIcon = '/assets/images/icons/heart-empty.svg';
const filledFavoriteIcon = '/assets/images/icons/heart-filled.svg';

type MerchantProductOfferCardProps = {
    offer: MerchantProductOffer;
    merchantIdToMerchantNameMap: MerchantIdToMerchantNameMap;
    savedMerchantProductOffers: MerchantProductOffer[] | null;
    setSavedMerchantProductOffers: React.Dispatch<React.SetStateAction<MerchantProductOffer[]>>;
    isFavoritesTab?: boolean;
    unsavedMerchantProductOffersStillDisplayed?: MerchantProductOffer[];
    setUnsavedMerchantProductOffersStillDisplayed?: React.Dispatch<React.SetStateAction<MerchantProductOffer[]>>;
} & (
    | {
          isFavoritesTab: true;
          conversationId?: undefined;
          messageId?: undefined;
      }
    | {
          isFavoritesTab: false;
          conversationId: string;
          messageId: string;
      }
);

export function MerchantProductOfferCard({
    offer,
    merchantIdToMerchantNameMap,
    conversationId,
    messageId,
    savedMerchantProductOffers,
    setSavedMerchantProductOffers,
    isFavoritesTab,
    unsavedMerchantProductOffersStillDisplayed,
    setUnsavedMerchantProductOffersStillDisplayed,
}: MerchantProductOfferCardProps) {
    const apolloClient = useApolloClient();
    const handleFavoriteButtonClick = useHandleFavoriteButtonClick({
        offer,
        setSavedMerchantProductOffers,
        setUnsavedMerchantProductOffersStillDisplayed,
        isFavoritesTab,
        apolloClient,
    });
    const { isImageLoading, imageUrl, onImageLoad, onImageError } = useProductImage(offer);
    const [showFavoriteButtonWhenImageIsLoading, setShowFavoriteButtonWhenImageIsLoading] = React.useState(false);
    React.useEffect(() => {
        setTimeout(() => {
            setShowFavoriteButtonWhenImageIsLoading(true);
        }, 5000);
    }, []);
    if (!offer.processedWebpageUrl) return null;
    const merchantNameToDisplay =
        offer.merchantId && merchantIdToMerchantNameMap[offer.merchantId]
            ? merchantIdToMerchantNameMap[offer.merchantId]
            : offer.contextualInformation.originalMerchantName;
    // A merchant product offer is saved if it is in the 'savedMerchantProductOffers' array and it is not in the 'unsavedMerchantProductOffersStillDisplayed' array.
    const isUnsavedProductStillDisplayed = unsavedMerchantProductOffersStillDisplayed?.some(
        ({ merchantProductOfferId }) => merchantProductOfferId === offer.merchantProductOfferId
    );
    const isSaved =
        !!savedMerchantProductOffers?.some(
            ({ merchantProductOfferId }) => merchantProductOfferId === offer.merchantProductOfferId
        ) && !isUnsavedProductStillDisplayed;
    return (
        <ProductCardContainer
            onClick={() => {
                window.open(offer.processedWebpageUrl as string, '_blank');
                logUserEventUtil(apolloClient, {
                    type: 'clickedSuggestedProductOnJokoAi',
                    payload: isFavoritesTab
                        ? {
                              originTab: 'favorites',
                              merchantProductOfferId: offer.merchantProductOfferId ?? undefined,
                              merchantId: offer.merchantId ?? undefined,
                          }
                        : {
                              originTab: 'exploration',
                              conversationId: conversationId as string,
                              merchantProductOfferId: offer.merchantProductOfferId ?? undefined,
                              merchantId: offer.merchantId ?? undefined,
                              messageId: messageId as string,
                          },
                });
            }}
            data-debug={offer.merchantProductOfferId ?? undefined}>
            <ProductCardContent>
                {isImageLoading ? (
                    <ProductImageContainer $isImageLoading={isImageLoading} $isImagePlaceholder={true}>
                        {/* TODO: refactor to remove React Native components */}
                        <AnimatedGradientBackground />
                        {showFavoriteButtonWhenImageIsLoading ? (
                            <FavoriteButton
                                onClick={(event) =>
                                    handleFavoriteButtonClick({
                                        event,
                                        isSaved,
                                    })
                                }>
                                <FavoriteButtonIcon src={isSaved ? filledFavoriteIcon : emptyFavoriteIcon} />
                            </FavoriteButton>
                        ) : null}
                    </ProductImageContainer>
                ) : null}
                <ProductImageContainer $isImageLoading={isImageLoading}>
                    {imageUrl ? (
                        <ProductImageWrapper>
                            <ProductImage
                                src={imageUrl}
                                onLoad={() => onImageLoad()}
                                onError={() => onImageError(imageUrl)}
                            />
                            <FavoriteButton
                                onClick={(event) =>
                                    handleFavoriteButtonClick({
                                        event,
                                        isSaved,
                                    })
                                }>
                                <FavoriteButtonIcon src={isSaved ? filledFavoriteIcon : emptyFavoriteIcon} />
                            </FavoriteButton>
                        </ProductImageWrapper>
                    ) : null}
                </ProductImageContainer>
                <ProductTextContainer>
                    <ProductPriceText>
                        {formatCurrencyAmount(offer.priceInformation.displayPriceAmount.valueInCents / 100)}
                    </ProductPriceText>
                    <ProductTitleText>{offer.originalTitle}</ProductTitleText>
                    {merchantNameToDisplay ? (
                        <ProductLinkContainer>
                            <ProductLinkText>{merchantNameToDisplay}</ProductLinkText>
                            <LinkArrowImage src={productLinkArrow} />
                        </ProductLinkContainer>
                    ) : null}
                </ProductTextContainer>
            </ProductCardContent>
        </ProductCardContainer>
    );
}

export function MerchantProductOfferPlaceholderCards({ viewportWidth }: { viewportWidth: number }) {
    const numberOfCards =
        viewportWidth < getPixelNumberFromDeviceWidthMeasurement(deviceWidthMeasurements.smallMedium!)
            ? 2
            : viewportWidth < getPixelNumberFromDeviceWidthMeasurement(deviceWidthMeasurements.large!)
            ? 6
            : 15;
    return (
        <>
            {Array.from({
                length: numberOfCards,
            }).map((_, index) => (
                <MerchantProductOfferPlaceholderCard key={index} />
            ))}
        </>
    );
}

export function MerchantProductOfferPlaceholderCard() {
    return (
        <ProductCardContainer $isPlaceholder={true}>
            <ProductCardContent>
                <ProductImageContainer $isImageLoading={false}>
                    <ProductImagePlaceholder />
                </ProductImageContainer>
                <ProductTextContainer>
                    <ProductPricePlaceholder />
                    <ProductTitlePlaceholder>
                        <RectanglePlaceHolder />
                        <RectanglePlaceHolder />
                    </ProductTitlePlaceholder>
                    <ProductLinkPlaceholder />
                </ProductTextContainer>
            </ProductCardContent>
        </ProductCardContainer>
    );
}

const useHandleFavoriteButtonClick = ({
    offer,
    setSavedMerchantProductOffers,
    setUnsavedMerchantProductOffersStillDisplayed,
    isFavoritesTab,
    apolloClient,
}: {
    offer: MerchantProductOffer;
    setSavedMerchantProductOffers: React.Dispatch<React.SetStateAction<MerchantProductOffer[]>>;
    setUnsavedMerchantProductOffersStillDisplayed?: React.Dispatch<React.SetStateAction<MerchantProductOffer[]>>;
    isFavoritesTab: boolean;
    apolloClient: ApolloClient<object>;
}) => {
    const isSandboxEnvironment = !!useHasFeature(Feature.useProductDiscoverySandboxEnvironment);
    const saveMerchantProductOffer = useSaveMerchantProductOffer();
    const unsaveMerchantProductOffer = useUnsaveMerchantProductOffer();
    return async ({ event, isSaved }: { event: React.MouseEvent; isSaved: boolean }) => {
        event.stopPropagation();
        if (isSaved)
            await handleUnsaveMerchantProductOffer({
                offer,
                setSavedMerchantProductOffers,
                setUnsavedMerchantProductOffersStillDisplayed,
                unsaveMerchantProductOffer,
                isSandboxEnvironment,
                isFavoritesTab,
                apolloClient,
            });
        else
            await handleSaveMerchantProductOffer({
                offer,
                setSavedMerchantProductOffers,
                saveMerchantProductOffer,
                setUnsavedMerchantProductOffersStillDisplayed,
                isFavoritesTab,
                isSandboxEnvironment,
                apolloClient,
            });
    };
};

const ProductCardContainer = styled.div<{
    $isPlaceholder?: boolean | undefined;
}>`
    position: relative;
    display: flex;
    flex-direction: column;
    border-radius: 16px;
    min-width: ${isMobileDevice ? '' : '100%'};
    max-width: ${({ $isPlaceholder }) => ($isPlaceholder ? '100%' : 'max-content')};

    @media (min-width: ${deviceWidthMeasurements.small}) {
        ${({ $isPlaceholder }) =>
            !$isPlaceholder &&
            `
            ${CLICKABLE_ELEMENT_CSS_MIXIN}
        `}
    }

    @media (max-width: ${deviceWidthMeasurements.small}) {
        max-width: 167px;
        background-color: ${colors.background.default};
    }
`;

const ProductCardContent = styled.div``;

const ProductImageContainer = styled.div<{ $isImageLoading: boolean; $isImagePlaceholder?: boolean }>`
    display: ${({ $isImageLoading, $isImagePlaceholder }) => ($isImageLoading && !$isImagePlaceholder ? 'none' : 'flex')};
    align-items: center;
    margin-bottom: 12px;
    position: relative;

    @media (max-width: ${deviceWidthMeasurements.small}) {
        width: 167px;
        height: 167px;
    }
`;

const ProductTextContainer = styled.div`
    padding: 0 12px 12px 12px;

    @media (max-width: ${deviceWidthMeasurements.small}) {
        padding: 0px 8px;
    }
`;

const ProductLinkContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-top: 8px;

    @media (max-width: ${deviceWidthMeasurements.small}) {
        margin-top: 4px;
    }
`;

const ProductImageWrapper = styled.div`
    aspect-ratio: 1;
    width: 100%;
    position: relative;
`;

const ProductImage = styled.img`
    max-width: 100%;
    max-height: 100%;
    border-radius: 12px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`;

const FavoriteButton = styled.button`
    position: absolute;
    bottom: 8px;
    right: 8px;
    background: white;
    border-radius: 999px;
    border: none;
    padding: 14px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        box-shadow: 0px 0px 25px -5px rgba(0, 0, 0, 0.1), 0px 8px 10px -6px rgba(0, 0, 0, 0.1);
    }

    &:active {
        box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.1), 0px 1px 2px -1px rgba(0, 0, 0, 0.1);
    }
`;

const ProductImagePlaceholder = styled.div`
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 12px;
    background-color: ${colors.background.subtle};
    aspect-ratio: 1;
`;

const LinkArrowImage = styled.img`
    width: 11px;
    height: 11px;
    object-fit: contain;
`;

const FavoriteButtonIcon = styled.img`
    width: 16px;
    height: 16px;
    -webkit-user-drag: none;

    ${NON_HIGHLIGHTABLE_NOR_DRAGGABLE_ELEMENT_CSS_MIXIN}
`;

const ProductPriceText = styled.div`
    font-size: 18px;
    font-family: ${font.ambitBold};

    @media (max-width: ${deviceWidthMeasurements.small}) {
        font-size: 16px;
    }
`;

const ProductPricePlaceholder = styled.div`
    justify-content: left;
    width: 40%;
    height: 20px;
    border-radius: 20px;
    background-color: ${colors.background.subtle};
`;

const ProductTitleText = styled.div`
    display: -webkit-box;
    margin-top: 4px;
    font-size: 16px;
    font-family: ${font.ambitSemiBold};
    color: ${colors.content.secondary};
    overflow: hidden;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;

    @media (max-width: ${deviceWidthMeasurements.small}) {
        font-size: 14px;
    }
`;

const ProductTitlePlaceholder = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-top: 8px;
    gap: 6px;
`;

const RectanglePlaceHolder = styled.div`
    width: 100%;
    height: 12px;
    border-radius: 20px;
    background-color: ${colors.background.subtle};
`;

const ProductLinkText = styled.div`
    display: -webkit-box;
    margin-right: 8px;
    font-size: 14px;
    font-family: ${font.ambitSemiBold};
    text-decoration-line: underline;
    overflow: hidden;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
`;

const ProductLinkPlaceholder = styled.div`
    width: 50%;
    height: 16px;
    margin-top: 8px;
    border-radius: 20px;
    background-color: ${colors.background.subtle};
`;
